/**
 * Product-level quantity max enforcement for custom.cart_quantity_max.
 * - metafield empty/0 => script not loaded (native store rules apply)
 * - blocks rapid repeated adds above max
 * - does not modify product-form.js or cart.js
 */
const rbCartQuantityMaxControllers = new Set();
const PRODUCT_FORM_SELECTOR = 'product-form[data-cart-quantity-max]';

function parseIntOrZero(value) {
  const parsed = parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

class RbCartQuantityMax {
  constructor(productForm) {
    this.productForm = productForm;
    this.form = productForm.querySelector('form[data-type="add-to-cart-form"]');
    this.submitButton = productForm.querySelector('button[type="submit"][name="add"]');
    this.submitButtonText =
      productForm.querySelector('.product-form__submit-label') || this.submitButton?.querySelector('span');
    this.paymentButton = productForm.querySelector('.shopify-payment-button');

    if (!this.form || !this.submitButton) return;

    this.pendingAdds = 0;
    this.form.addEventListener('submit', this.onSubmitCapture.bind(this), true);
    productForm.dataset.rbCartQuantityMaxInit = 'true';
    rbCartQuantityMaxControllers.add(this);
  }

  get max() {
    return parseIntOrZero(this.productForm.dataset.cartQuantityMax);
  }

  get productId() {
    return Number(this.productForm.dataset.productId);
  }

  quantityInCart(cartData) {
    if (!this.productId) return 0;
    if (cartData?.items?.length) {
      return cartData.items
        .filter((item) => Number(item.product_id) === this.productId)
        .reduce((sum, item) => sum + item.quantity, 0);
    }
    return parseIntOrZero(this.productForm.dataset.cartProductQuantity);
  }

  setQuantityInCart(quantity) {
    this.productForm.dataset.cartProductQuantity = String(quantity);
  }

  isAtMax(cartData) {
    const max = this.max;
    if (!max) return false;
    return this.quantityInCart(cartData) + this.pendingAdds >= max;
  }

  getRequestedQuantity() {
    const formData = new FormData(this.form);
    return Math.max(1, parseIntOrZero(formData.get('quantity')) || 1);
  }

  wouldExceedMax(requestedQuantity, cartData) {
    const max = this.max;
    if (!max) return false;
    return this.quantityInCart(cartData) + this.pendingAdds + requestedQuantity > max;
  }

  isVariantUnavailable() {
    return this.productForm.querySelector('[name="id"]')?.hasAttribute('disabled');
  }

  onSubmitCapture(evt) {
    const max = this.max;
    if (!max) return;
    const requestedQuantity = this.getRequestedQuantity();

    if (this.isAtMax() || this.wouldExceedMax(requestedQuantity)) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      this.syncButtonState();
      return;
    }

    this.pendingAdds += requestedQuantity;
  }

  syncFromCart(cartData) {
    this.setQuantityInCart(this.quantityInCart(cartData));
    this.pendingAdds = 0;
    this.syncButtonState(cartData);
  }

  syncButtonState(cartData) {
    const max = this.max;
    if (!max || !this.submitButton) return;

    if (this.isAtMax(cartData)) {
      this.submitButton.setAttribute('disabled', 'disabled');
      this.submitButton.setAttribute('aria-disabled', 'true');
      if (this.submitButtonText && this.productForm.dataset.labelAlreadyInCart) {
        this.submitButtonText.textContent = this.productForm.dataset.labelAlreadyInCart;
      }
      this.paymentButton?.setAttribute('hidden', '');
      return;
    }

    this.paymentButton?.removeAttribute('hidden');
    if (!this.isVariantUnavailable()) {
      this.submitButton.removeAttribute('disabled');
      this.submitButton.removeAttribute('aria-disabled');
      if (this.submitButtonText && this.productForm.dataset.labelAddToCart) {
        this.submitButtonText.textContent = this.productForm.dataset.labelAddToCart;
      }
    }
  }
}

function initProductForms() {
  document.querySelectorAll(PRODUCT_FORM_SELECTOR).forEach((el) => {
    if (el.dataset.rbCartQuantityMaxInit) return;
    new RbCartQuantityMax(el);
  });
}

function syncAllProductForms(cartData) {
  initProductForms();
  rbCartQuantityMaxControllers.forEach((c) => c.syncFromCart(cartData));
}

function clampCartLineQuantities(root = document) {
  const inputs = Array.from(
    root.querySelectorAll('.quantity__input[data-rb-cart-product-id][data-rb-cart-max][data-index]')
  );

  const productGroups = new Map();
  inputs.forEach((input) => {
    const productId = input.dataset.rbCartProductId;
    const max = parseIntOrZero(input.dataset.rbCartMax);
    const quantity = parseIntOrZero(input.value);
    if (!productId || !max) return;

    if (!productGroups.has(productId)) {
      productGroups.set(productId, { max, total: 0, inputs: [] });
    }
    const group = productGroups.get(productId);
    group.total += quantity;
    group.inputs.push(input);
  });

  productGroups.forEach((group) => {
    if (group.total <= group.max) return;

    let overflow = group.total - group.max;
    const orderedInputs = group.inputs.sort(
      (a, b) => parseIntOrZero(b.dataset.index) - parseIntOrZero(a.dataset.index)
    );

    orderedInputs.forEach((input) => {
      if (overflow <= 0) return;
      const current = parseIntOrZero(input.value);
      if (current <= 0) return;

      const reduceBy = Math.min(current, overflow);
      const target = current - reduceBy;
      overflow -= reduceBy;

      const cartItemsEl = input.closest('cart-items') || input.closest('cart-drawer-items');
      if (typeof cartItemsEl?.updateQuantity === 'function') {
        cartItemsEl.updateQuantity(input.dataset.index, target, input.name, input.dataset.quantityVariantId);
      }
    });
  });
}

function fetchCartAndSync() {
  if (typeof routes === 'undefined' || !routes.cart_url) return Promise.resolve();
  return fetch(`${routes.cart_url}.js`)
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to fetch cart'))))
    .then((cart) => {
      syncAllProductForms(cart);
      clampCartLineQuantities();
      return cart;
    })
    .catch(() => {});
}

let rbSubscribeAttempts = 0;
function ensureCartUpdateSubscription() {
  if (window._rbCartQuantityMaxSubscribed) return;
  if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') {
    if (rbSubscribeAttempts < 10) {
      rbSubscribeAttempts += 1;
      window.setTimeout(ensureCartUpdateSubscription, 150);
    }
    return;
  }
  window._rbCartQuantityMaxSubscribed = true;
  subscribe(PUB_SUB_EVENTS.cartUpdate, () => queueMicrotask(fetchCartAndSync));
  subscribe(PUB_SUB_EVENTS.cartError, fetchCartAndSync);
}

function initRbCartQuantityMax() {
  initProductForms();
  fetchCartAndSync();
  ensureCartUpdateSubscription();
}

function runInit() {
  initRbCartQuantityMax();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', runInit);
} else {
  runInit();
}
document.addEventListener('shopify:section:load', runInit);
window.addEventListener('load', runInit, { once: true });

